import { registerDevice as registerDeviceApi, deactivateDevice } from 'api/Notifications';

import { getDeviceId, getDeviceName } from './deviceId';
import { deletePushToken, getPermission, getPushToken, isPushSupported } from './messaging';

/**
 * The app-lifecycle side of push: turning a browser permission into a device
 * row on the backend, and tearing it down again on sign-out.
 */

const APP_VERSION = process.env.REACT_APP_VERSION || '';

/**
 * Last token this tab registered. Cached so the every-app-start refresh does
 * not re-POST an unchanged token on every route change, while a *rotated*
 * token still goes through.
 */
let lastRegisteredToken = null;

/**
 * Register (or refresh) this browser as a push device.
 *
 * Safe — and intended — to call on every app start: the POST refreshes
 * `last_seen_at` and carries any rotated FCM token under the unchanged
 * device_id so the row updates in place.
 *
 * Never prompts. Permission must already be granted (see `enablePush`).
 */
export const registerDevice = async ({ force = false } = {}) => {
  if (!(await isPushSupported())) return { status: 'unsupported' };
  if (getPermission() !== 'granted') return { status: 'denied' };

  const token = await getPushToken();
  if (!token) return { status: 'no-token' };

  if (!force && token === lastRegisteredToken) return { status: 'unchanged', token };

  try {
    const response = await registerDeviceApi({
      token,
      platform: 'web',
      device_id: getDeviceId(),
      device_name: getDeviceName(),
      ...(APP_VERSION && { app_version: APP_VERSION })
    });
    lastRegisteredToken = token;
    return { status: 'registered', device: response.data.data, token };
  } catch (error) {
    return { status: 'error', error };
  }
};

/**
 * Ask for permission, then register. The only path that prompts, so it must be
 * called from a user gesture — browsers ignore (and Chrome penalises)
 * permission prompts not tied to one.
 */
export const enablePush = async () => {
  if (!(await isPushSupported())) return { status: 'unsupported' };

  const { requestPermission } = await import('./messaging');
  const permission = await requestPermission();
  if (permission !== 'granted') return { status: 'denied' };

  return registerDevice({ force: true });
};

/**
 * Sign-out teardown. Deactivates the row server-side FIRST — the endpoint is
 * session-authenticated, so once the session is gone the row can never be
 * cleaned up and the backend keeps pushing platform alerts to a browser
 * nobody is signed into.
 *
 * Never throws, and time-boxed: sign-out awaits this, so nothing inside may
 * hang the operator on a dead screen. If it times out the backend's
 * UNREGISTERED prune cleans up.
 */
const TEARDOWN_TIMEOUT_MS = 4000;

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

export const disablePush = async () => {
  try {
    const token = await withTimeout(getPushToken(), TEARDOWN_TIMEOUT_MS);
    if (token) await withTimeout(deactivateDevice(token), TEARDOWN_TIMEOUT_MS);
  } catch (err) {
    console.warn('[push] deactivate failed', err);
  }

  try {
    await withTimeout(deletePushToken(), TEARDOWN_TIMEOUT_MS);
  } catch {
    /* already reported inside deletePushToken */
  }

  lastRegisteredToken = null;
};

export const resetPushRegistrationCache = () => {
  lastRegisteredToken = null;
};
